import { InfoRounded } from "@mui/icons-material";
import DoneRoundedIcon from "@mui/icons-material/DoneRounded";
import { Box, Divider, Drawer, IconButton, Typography } from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef, useState } from "react";
import useAPI from "../../../../api/hooks/useAPI";
import useColors from "../../../../api/hooks/useColors";
import { adminPermissionKeys } from "../../../../api/queryKeys";
import ExportButton from "../../../../components/buttons/ExportButton";
import StatusChip from "../../../../components/chips/StatusChip";
import CustomToolTip from "../../../../components/CustomTooltip";
import CustomTooltipBorder from "../../../../components/CustomTooltipBorder";
import LoadingIcon from "../../../../components/LoadingIcon";
import { useFileNaming } from "../../../../hooks/useFileNaming";
import { PING_STATUS } from "../../../../utils/constants";
import { getPrettyDateTime, useLocalTime } from "../../../../utils/timestamp";
import EmailPingStatus from "./EmailPingStatus";
import IntegrationChip from "./IntegrationChip";
import UserAdminDetails from "./UserAdminDetails";
import UserAdminPermissionsTable from "./UserAdminPermissionsTable";

const AdminUserDrawer = ({
  openDrawer,
  onClose,
  selectedUser,
  adminUsers,
  setOpenModal,
  circleColor,
  setUserToPing,
  handleHeSystemConnect,
}) => {
  const colors = useColors();
  const [selectedUserByTool, setSelectedUserByTool] = useState(selectedUser);
  const [updateResolvedIsLoading, setUpdateResolvedIsLoading] = useState(false);
  const { trackEvent, approveUserPermissions } = useAPI();
  const queryClient = useQueryClient();
  const gridRef = useRef(null);
  const { generateFileName } = useFileNaming();

  const showLocalTime = useLocalTime();

  const fullUserDetails = selectedUser
    ? adminUsers.filter((user) => user?.user_email === selectedUser?.user_email)
    : [];
  const toolPermissionsList = fullUserDetails.map((user) => ({
    last_seen_in_system: user.last_seen_in_system,
    tool: user.tool,
    admin_permissions: user.admin_permissions,
  }));

  useEffect(() => {
    if (selectedUser) setSelectedUserByTool(selectedUser);
  }, [selectedUser]);
  const handleTableExport = () => {
    if (!selectedUser || !selectedUserByTool) return;
    const message =
      'User click on "Export" button to download the permission admin user details from the admin user drawer';
    trackEvent(message);

    const csvExportOptions = {
      fileName: generateFileName(
        `admin_user_${selectedUser.name}_${selectedUserByTool.tool}`
      ),
    };

    gridRef?.current?.exportDataAsCsv(csvExportOptions);
  };

  const handleResolve = async () => {
    if (!selectedUser || !selectedUserByTool) return;
    const message =
      'User click on "Resolve the Event" button to resolve the event from the admin user drawer';
    trackEvent(message).then();

    setUpdateResolvedIsLoading(true);
    trackEvent("user approved permission for" + selectedUserByTool.tool_email);
    await approveUserPermissions(selectedUserByTool.tool_email).then(() => {
      queryClient.invalidateQueries({ queryKey: adminPermissionKeys.all });
    });
    setUpdateResolvedIsLoading(false);
  };

  return (
    <Drawer
      PaperProps={{
        style: {
          width: "600px",
          backgroundColor: colors.grey_50,
          borderRadius: "8px 0 0 0",
        },
      }}
      anchor="right"
      open={openDrawer}
      onClose={onClose}
    >
      <Box
        sx={{ padding: 3, display: "flex", flexDirection: "column", gap: 2 }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: "row",
            gap: 2,
            justifyContent: "space-between",
          }}
        >
          <Box>
            <Typography variant="h5">{selectedUser?.name}</Typography>
            <Typography variant="subtitle2">
              {selectedUser?.user_email}
            </Typography>
          </Box>

          <ExportButton handleExport={handleTableExport} />
        </Box>
        <Divider orientation="horizontal" />
        <UserAdminDetails
          user={selectedUser}
          handleHeSystemConnect={handleHeSystemConnect}
        />
        <Box
          sx={{
            display: "flex",
            flexDirection: "row",
            alignItems: "center",
            gap: 1,
            mt: 2,
          }}
        >
          <Typography variant="subtitle1">Privileged Access</Typography>
          <CustomToolTip
            title={
              <Typography variant="caption">
                Privileged Access data reflects information collected since
                Breeze started actively monitoring user permissions
              </Typography>
            }
          >
            <InfoRounded
              sx={{
                color: colors.primary_48percent,
                width: "16px",
              }}
            />
          </CustomToolTip>
        </Box>
        <Box
          sx={{
            display: "flex",
            flexDirection: "row",
            gap: "8px 8px",
            width: "100%",
            flexWrap: "wrap",
          }}
        >
          {toolPermissionsList.map((toolPermission, index) => (
            <IntegrationChip
              key={index}
              name={toolPermission.tool}
              selected={selectedUserByTool?.tool === toolPermission.tool}
              onChipClick={() => {
                const fullUser = fullUserDetails.find(
                  (user) => user.tool === toolPermission.tool
                );
                setSelectedUserByTool(fullUser);
              }}
              cancelSelected={false}
              displayToolName={false}
            />
          ))}
        </Box>
        {selectedUserByTool?.last_seen_in_system?.timestamp_utc && (
          <Box display={"flex"} flexDirection={"column"} gap={1}>
            <Typography variant="overline">{"Last Seen In System"}</Typography>
            <Typography variant="caption">
              {getPrettyDateTime(
                selectedUserByTool?.last_seen_in_system?.timestamp_utc,
                showLocalTime
              )}
            </Typography>
          </Box>
        )}

        <Box
          display={"flex"}
          flexDirection={"column"}
          gap={1}
          alignItems={"flex-start"}
        >
          <Typography variant="overline">
            {"Permissions Approval Status"}
          </Typography>
          <Box
            sx={{
              display: "flex",
              flexDirection: "row",
              gap: 1,
            }}
          >
            <CustomTooltipBorder
              title={
                <EmailPingStatus
                  status_details={selectedUserByTool?.ping_status}
                  setOpenModal={setOpenModal}
                  userPermissionDetails={selectedUserByTool}
                  setUserToPing={setUserToPing}
                />
              }
              borderColor={
                PING_STATUS.PENDING === selectedUser?.ping_status.status
                  ? colors.common_white
                  : circleColor[selectedUser?.ping_status]
              }
            >
              <Box style={{ whiteSpace: "nowrap", width: "" }}>
                <StatusChip
                  circleColor={circleColor[selectedUser?.ping_status.status]}
                  label={selectedUser?.ping_status.status}
                />
              </Box>
            </CustomTooltipBorder>

            {selectedUserByTool?.ping_status?.status !==
              PING_STATUS.APPROVED && (
              <IconButton
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  color: colors.primary_main,
                  border: "1px solid" + colors.grey_600,
                  height: "24px",
                  textTransform: "none",
                  borderRadius: "10px",
                  borderColor: colors.primary_main,
                  gap: "8px",
                }}
                onClick={handleResolve}
                disabled={updateResolvedIsLoading}
              >
                <Typography variant="overline"> Approve</Typography>
                {updateResolvedIsLoading ? (
                  <LoadingIcon size={10} />
                ) : (
                  <DoneRoundedIcon
                    sx={{
                      width: "16px",
                    }}
                  />
                )}
              </IconButton>
            )}
          </Box>
        </Box>
        {!selectedUserByTool?.is_active && (
          <Typography variant="overline" sx={{ width: "65%" }}>
            This user no longer has admin permissions for this tool. Previous
            permissions are displayed below for reference.
          </Typography>
        )}

        <UserAdminPermissionsTable
          adminPermissions={selectedUserByTool?.admin_permissions}
          gridRef={gridRef}
        />
      </Box>
    </Drawer>
  );
};

export default AdminUserDrawer;
